import { DishAllergen } from "ts-packages/api-types/src";

import {
  Fragment,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { ArrowBackRounded } from "@mui/icons-material";

import {
  DynamicDrawer,
  PreferenceChipOutlined,
  Search,
  SpiceLevelIcon,
  TagChip,
  TickIcon,
  isSpiceLevel,
} from "igfd-ui/src";

import { useRestaurantContext } from "../../hooks/useRestaurantContext";
import {
  type MenuTreeNode,
  isValidGuestGroupHeader,
} from "../../menu/menu-tree";
import { allergenBands } from "../../menu/staff-allergen-bands";
import { staffMenuCopy } from "../../menu/staff-menu-copy";
import {
  countChoiceGroups,
  countOptions,
  dishChoiceGroups,
} from "../../menu/staff-menu-tree";
import { trackStaffEvent } from "../../monitoring/mixpanel";
import { useSearchTracking } from "../../monitoring/useSearchTracking";
import { IngredientList } from "../ingredient-list/ingredient-list";
import { AllergenBandsCard } from "./allergen-bands";
import { ChoiceGroup } from "./choice-group";
import { BAND_ICON_SIZE, DrawerBand } from "./drawer-band";
import { ProductList } from "./product-list";

interface DishDrawerProps {
  open: boolean;
  node?: MenuTreeNode;
  onClose: () => void;
  onSelectProduct: (productId: string) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

/** One step of the drawer's history: a dish, and the group it was opened from. */
type DrawerStep = { node: MenuTreeNode; fromGroup: string | null };

/** Read aloud, never drawn. Clipped rather than hidden, which would silence it. */
const visuallyHidden = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute" as const,
  whiteSpace: "nowrap" as const,
  width: "1px",
};

/**
 * A named block of the drawer. Grey does the grouping so borders do not have
 * to: a bordered card inside a bordered card reads as two things when it is
 * one, and the label sits outside the fill where it titles the group rather
 * than joining it.
 */
const Section = ({
  label,
  trailing,
  children,
}: PropsWithChildren<{ label?: string; trailing?: ReactNode }>) => {
  const {
    palette: { oscar },
  } = useTheme();

  return (
    <Stack flexShrink={0} gap={1} marginTop={3} width="100%">
      {/* One step above the sub-headings inside it, so the nesting is legible
          from type alone rather than from where the greys start and stop. */}
      {label || trailing ? (
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          gap={1}
          justifyContent="space-between"
        >
          {label ? (
            <Typography variant="labelRMedium">{label}</Typography>
          ) : null}
          {trailing}
        </Stack>
      ) : null}
      <Stack
        sx={{
          backgroundColor: oscar.light.surface.neutral,
          borderRadius: 4,
          flexShrink: 0,
          gap: 3,
          padding: 3,
          width: "100%",
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};

export const DishDrawer = ({
  open,
  node,
  onClose,
  onSelectProduct,
  containerRef,
}: DishDrawerProps) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();

  const [filter, setFilter] = useState("");
  const [selectedAllergenIds, setSelectedAllergenIds] = useState<string[]>([]);
  const { linkedItems, priceFormatter } = useRestaurantContext();

  /**
   * An option is a dish, so opening one replaces what the drawer shows rather
   * than stacking a second drawer on the first. One sheet at a time from the
   * list; the product drawer stays the only layer that opens over this.
   */
  const [pushed, setPushed] = useState<DrawerStep[]>([]);

  // Derived rather than synced through an effect: a stack reset one render
  // behind the dish would paint the previous dish's options over the new one
  // for a frame, and the drawer stays mounted between opens.
  const stack: DrawerStep[] =
    node && pushed[0]?.node.id === node.id
      ? pushed
      : node
      ? [{ node, fromGroup: null }]
      : [];

  const step = stack[stack.length - 1];
  const current = step?.node;
  const parentGroup = step?.fromGroup ?? null;
  const previousDishName = stack[stack.length - 2]?.node.dish?.name;
  const dish = current?.dish;
  const linkedDish = dish && linkedItems?.dishes[dish.id];

  // A depth-zero group opens here too. It offers options and has bands, but no
  // recipe of its own, so its own children are the choices rather than the
  // groups a dish owns.
  const rootHeader =
    current?.kind === "header" && isValidGuestGroupHeader(current.header)
      ? current.header
      : null;
  const isGroup = current?.kind === "header";
  // A group has its own words too, and the drawer used to read the dish only,
  // so a choice opened from the menu lost whatever the restaurant wrote.
  const nodeDescription = isGroup ? rootHeader?.description : dish?.description;

  const groups = useMemo(
    () => (!current ? [] : isGroup ? [current] : dishChoiceGroups(current)),
    [current, isGroup]
  );
  // Computed for every node, not only those with choices: it is the drawer's
  // single allergen surface now, so a plain dish reads from it too.
  const bands = useMemo(
    () => (current ? allergenBands(current) : null),
    [current]
  );
  // Named from the bands rather than the ids, so the reminder reads the way the
  // chips a waiter ticked read.
  const selectedAllergenNames = useMemo(() => {
    if (!bands || selectedAllergenIds.length === 0) return [];
    const selected = new Set(selectedAllergenIds);
    return [...bands.always, ...bands.depends, ...bands.mayContain]
      .filter((allergen) => selected.has(allergen.igfdTaxAllergenId))
      .map((allergen) => allergen.allergenName)
      .filter((name, index, names) => names.indexOf(name) === index);
  }, [bands, selectedAllergenIds]);

  const directItemCount =
    (linkedDish?.directlyLinkedIngredients?.length || 0) +
    (linkedDish?.directlyLinkedProducts?.length || 0);

  // The drawer is never unmounted, so without this the stack survives dismissal
  // and reopening the same dish reopens the option last stepped into.
  const handleClose = useCallback(() => {
    setPushed([]);
    onClose();
  }, [onClose]);

  const handleSelectAllergen = useCallback(
    (allergen: DishAllergen) => {
      if (!selectedAllergenIds.includes(allergen.igfdTaxAllergenId)) {
        trackStaffEvent("Allergen Checked", {
          allergen_id: allergen.igfdTaxAllergenId,
          allergen_name: allergen.allergenName,
          where: "dish",
        });
      }
      setSelectedAllergenIds((prevIds) =>
        prevIds.includes(allergen.igfdTaxAllergenId)
          ? prevIds.filter((id) => id !== allergen.igfdTaxAllergenId)
          : [...prevIds, allergen.igfdTaxAllergenId]
      );
    },
    [selectedAllergenIds]
  );
  useSearchTracking("ingredients", filter);

  // Reset filtering and selected allergens whenever the drawer changes what it
  // is showing, including a step into an option and back out again. Filter
  // state belonging to the bun must not survive onto the burger.
  useEffect(() => {
    setSelectedAllergenIds([]);
    setFilter("");
    // Keyed on the node, not the dish: a group has no dish of its own, and two
    // groups opened in turn must not share a selection.
  }, [current?.id]);

  const iconButtonSx = {
    borderRadius: "12px",
    height: 44,
    width: 44,
    "&:focus-visible": {
      outline: `3px solid ${oscar.light.icon.link}`,
      outlineOffset: "2px",
    },
  };

  // Where this sits in the dish: which group it came from, or what it offers.
  // An option names its group and nothing more; the Back button above already
  // says which dish, so spelling out "An option in" only made the chip long.
  const structureLine = rootHeader
    ? staffMenuCopy.groupMeta(
        rootHeader.required,
        current ? countOptions(current) : 0,
        current ? countChoiceGroups(current) : 0
      )
    : parentGroup
    ? parentGroup
    : groups.length > 0
    ? staffMenuCopy.choices(groups.length)
    : null;

  // The facts a waiter gets asked for second, each its own item so the rule
  // between them is drawn rather than punctuated. An accordion used to make
  // these a tap, and put the dish's own name inside itself.
  const headerFacts: ReactNode[] = [
    structureLine ? (
      <Chip
        key="structure"
        // Short on screen, complete to a screen reader, which has no Back
        // button in view to borrow the context from. Spoken by a visually
        // hidden span rather than aria-label: this Chip has no onClick, so MUI
        // renders a role-less div, and aria-label on one of those is ignored.
        label={
          <>
            <Box aria-hidden>{structureLine}</Box>
            {parentGroup ? (
              <Box sx={visuallyHidden}>
                {staffMenuCopy.optionIn(parentGroup)}
              </Box>
            ) : null}
          </>
        }
        size="small"
        sx={{
          backgroundColor: oscar.light.surface.gray,
          color: oscar.light.content.content1,
          ...typography.labelRMedium,
        }}
      />
    ) : null,
    dish?.priceInLowestDenomination && priceFormatter ? (
      <Typography
        key="price"
        variant="labelRMedium"
        color={oscar.light.content.content1}
      >
        {priceFormatter(dish.priceInLowestDenomination)}
      </Typography>
    ) : null,
    dish?.caloriesInKcal ? (
      <Typography
        key="calories"
        variant="labelRMedium"
        color={oscar.light.content.content1}
      >
        {staffMenuCopy.calories(dish.caloriesInKcal)}
      </Typography>
    ) : null,
    isSpiceLevel(dish?.spiceLevel) ? (
      <SpiceLevelIcon key="spice" level={dish?.spiceLevel} />
    ) : null,
  ].filter(Boolean);

  const DrawerHeader = (
    <Stack
      gap={1}
      mb={1}
      width="100%"
      sx={{
        // Mirrors DynamicDrawer's own gutter, which turning off its header
        // padding also removed. Kept as raw queries because its breakpoints
        // are not the theme's, and the title has to line up with the cards.
        paddingTop: 3,
        paddingX: 3,
        "@media (min-width:321px)": { paddingX: 6 },
        "@media (min-width:501px)": { paddingX: 8 },
      }}
    >
      {/* Back only. Close lives in the footer, and a second dismiss up here
          would be the button people mistake for it. */}
      {stack.length > 1 ? (
        <ButtonBase
          type="button"
          onClick={() => setPushed(stack.slice(0, -1))}
          aria-label={staffMenuCopy.backTo(
            previousDishName ?? staffMenuCopy.backFallback
          )}
          sx={{
            ...iconButtonSx,
            alignSelf: "flex-start",
            gap: 0.5,
            paddingX: 1,
            width: "auto",
          }}
        >
          <ArrowBackRounded
            fontSize="small"
            sx={{ color: oscar.light.content.link }}
          />
          <Typography variant="labelRMedium" color={oscar.light.content.link}>
            {previousDishName ?? staffMenuCopy.backFallback}
          </Typography>
        </ButtonBase>
      ) : null}

      <Stack gap={0.5}>
        <Typography variant="headingRLight">
          {dish?.name ?? rootHeader?.title}
        </Typography>
        {/* One line of facts, not three. The chip is the one the menu list
            already uses for this dish, so what a waiter tapped is what greets
            them. */}
        {headerFacts.length ? (
          <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            gap={1.5}
            paddingTop={0.5}
          >
            {headerFacts.map((fact, index) => (
              <Fragment key={index}>
                {index > 0 ? (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: oscar.light.borders.gray, marginY: 0.5 }}
                  />
                ) : null}
                {fact}
              </Fragment>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );

  const DrawerFooter = (
    <ButtonBase
      sx={{
        backgroundColor: oscar.light.surface.black,
        borderRadius: 32,
        height: 48,
        width: 168,
      }}
      onClick={handleClose}
    >
      <Typography
        color={oscar.light.content.clear}
        variant="buttonL"
        textAlign="center"
      >
        {staffMenuCopy.close}
      </Typography>
    </ButtonBase>
  );

  if (!dish && !rootHeader)
    return (
      <DynamicDrawer open={false} drawerType="large" onClose={handleClose} />
    );

  return (
    <DynamicDrawer
      ref={containerRef}
      drawerType="large"
      open={open}
      onClose={handleClose}
      Header={DrawerHeader}
      Footer={DrawerFooter}
      // Off for the headroom, not the gutter: this flag controls both, so the
      // header re-applies the side padding itself. See DrawerHeader.
      hasHeaderPadding={false}
      // Content passes under the footer instead of stopping dead at it, so a
      // full sheet looks scrollable rather than finished.
      hasFooterBlur
    >
      {/* In the body, not the header: DynamicDrawer renders the header outside
          the scrolling column, so a long description there sat on screen
          permanently and squeezed everything else into what was left. */}
      {nodeDescription ? (
        <Typography
          flexShrink={0}
          // The drawer body centres its children, so without this the sentence
          // sits shrink-wrapped in the middle instead of ranged left.
          width="100%"
          variant="bodyRLight"
          color={oscar.light.content.content1}
        >
          {nodeDescription}
        </Typography>
      ) : null}

      {/* What this dish is, before what a waiter can do about it. A group has
          none of this: it is a question the dish asks, not something cooked. */}
      {dish ? (
        <Section label={staffMenuCopy.sectionRecipeInfo}>
          {/* Exactly one allergen surface. Two of them was the bug: milk read
              "depends on the choice" in one card and "may contain" in another,
              with nothing on screen saying which applied to what. */}
          {/* The three band headings name themselves, so a heading over them
              saying "Allergens" only adds a line to read. */}
          {bands ? (
            <AllergenBandsCard
              bands={bands}
              hasGroups={groups.length > 0}
              scope="dish"
              selectedAllergenIds={selectedAllergenIds}
              onSelectAllergen={handleSelectAllergen}
            />
          ) : null}

          {/* Built here rather than with PreferenceDetected, which paints its
              own card and padding: nested in this section it indented itself
              off the band headings. That component is shared with the product
              drawer, so it stays as it is. */}
          {linkedDish?.mostRestrictivePreference || dish.tags?.length ? (
            <DrawerBand
              label={staffMenuCopy.sectionDiet}
              icon={
                <TickIcon
                  height={BAND_ICON_SIZE}
                  width={BAND_ICON_SIZE}
                  // The filled circle carries a white tick, so the darker step
                  // is the one that holds it legibly.
                  color={oscar.light.icon.green700}
                />
              }
            >
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {linkedDish?.mostRestrictivePreference ? (
                  <PreferenceChipOutlined
                    preferenceName={
                      linkedDish.mostRestrictivePreference.preferenceName
                    }
                  />
                ) : null}
                {dish.tags?.map((tag) => (
                  <TagChip key={tag.id} tag={tag} />
                ))}
              </Stack>
            </DrawerBand>
          ) : null}

          {/* A dish built entirely from choices has no recipe of its own, so
              "0 items" over an empty search is noise. A plain dish with nothing
              linked still says so: there the zero is a gap in the data rather
              than the shape of the dish. */}
          {directItemCount > 0 || groups.length === 0 ? (
            <Stack flexShrink={0} gap={1} width="100%">
              <Typography variant="labelRMedium">
                {`${directItemCount} items in this dish`}
              </Typography>
              <Search
                filter={filter}
                setFilter={setFilter}
                placeholder="Search ingredients"
              />
              <ProductList
                dishId={dish.id}
                filter={filter}
                optionalProductIds={dish.optionalProductIds}
                selectedAllergenIds={selectedAllergenIds}
                onProductClick={onSelectProduct}
              />
              <IngredientList
                filter={filter}
                ingredients={linkedDish?.allIngredients}
                directlyLinkedIngredients={
                  linkedDish?.directlyLinkedIngredients
                }
                selectedAllergenIds={selectedAllergenIds}
                optionalIngredientIds={linkedDish?.optionalIngredientIds}
              />
            </Stack>
          ) : null}
        </Section>
      ) : null}

      {/* A group opened straight from the menu has no recipe above it, so its
          own options are all there is to show and the band card comes here. In
          a Section like every other block: the card stopped painting its own
          grey when it moved inside one, so bare it sat on the sheet unpadded. */}
      {isGroup && bands ? (
        <Section>
          <AllergenBandsCard
            bands={bands}
            hasGroups
            scope="group"
            selectedAllergenIds={selectedAllergenIds}
            onSelectAllergen={handleSelectAllergen}
          />
        </Section>
      ) : null}

      {groups.length > 0 ? (
        <Section
          label={isGroup ? undefined : staffMenuCopy.sectionChoices}
          // The allergen chips are a scroll away by the time a waiter reads the
          // options, so the question being asked is restated where the answers
          // are, rather than remembered.
          trailing={
            selectedAllergenNames.length ? (
              <Typography variant="labelS" color={oscar.light.content.content1}>
                {staffMenuCopy.checkingFor(selectedAllergenNames)}
              </Typography>
            ) : null
          }
        >
          <Stack
            sx={{
              // No fill of its own: the section grey already groups these, and
              // a white block inside it read as a second card.
              borderRadius: 4,
              // The drawer body is a scrolling column, so a child that can
              // shrink does: this collapsed to its own two border pixels and
              // `hidden` clipped every group out of the sheet.
              flexShrink: 0,
              overflow: "hidden",
              width: "100%",
            }}
          >
            {groups.map((group, index) => (
              <ChoiceGroup
                key={group.id}
                node={group}
                depth={0}
                isFirst={index === 0}
                selectedAllergenIds={selectedAllergenIds}
                defaultOpen={isGroup}
                hideHeader={isGroup}
                onOpenOption={(option, groupTitle) =>
                  setPushed([...stack, { node: option, fromGroup: groupTitle }])
                }
              />
            ))}
          </Stack>
        </Section>
      ) : null}
    </DynamicDrawer>
  );
};
